/**
 * Screener Phase 1 correctness helpers (audit 2026-10-27, docs/AUDIT_SCREENER_PAGE_2026-10-27.md).
 *
 * Pure functions only: no React, no fetch. Every number, state and word the Screener page fixed in Phase 1 is
 * derived here so it can be unit-tested against the real payload shapes (live run 2026-10-27 11:06 MT) and the
 * old wrong behaviour can be shown to fail. Nothing here ranks, sizes or places anything.
 */

/* ---------------------------------------------------------------------------------------------
 * Shared formatting (moved out of ScreenView.tsx so the export builders below can be tested).
 * ------------------------------------------------------------------------------------------- */

export const currency = (n: number | null | undefined) =>
  typeof n === "number"
    ? n.toLocaleString(undefined, { style: "currency", currency: "USD", maximumFractionDigits: 0 })
    : "—";

export const numberFmt = (n: number | null | undefined, d = 2) =>
  typeof n === "number" ? n.toLocaleString(undefined, { maximumFractionDigits: d }) : "—";

export const pctFmt = (n: number | null | undefined, d = 1) =>
  typeof n === "number" ? `${(n * 100).toLocaleString(undefined, { maximumFractionDigits: d })}%` : "—";

export function escHtml(value: any): string {
  return String(value ?? "")
    .replace(/&/g, "&amp;")
    .replace(/</g, "&lt;")
    .replace(/>/g, "&gt;")
    .replace(/"/g, "&quot;");
}

export function titleizeCode(value?: string | null): string {
  if (!value) return "—";
  return String(value)
    .replaceAll("_", " ")
    .replaceAll("-", " ")
    .replace(/\b\w/g, (c) => c.toUpperCase());
}

export function titleizeBucket(bucket?: string | null): string {
  if (!bucket) return "—";
  switch (bucket) {
    case "18_24m":
      return "18–24m";
    case "24_30m":
      return "24–30m";
    case "30m_plus":
      return "30m+";
    case "under_18m":
      return "Under 18m";
    default:
      return bucket.replaceAll("_", " ");
  }
}

const MONTHS = ["JAN", "FEB", "MAR", "APR", "MAY", "JUN", "JUL", "AUG", "SEP", "OCT", "NOV", "DEC"];

/** "2030-12-20" -> "DEC2030" (same convention as the ladder panel). */
export function expiryMonLabel(iso: string | null | undefined): string {
  const m = /^(\d{4})-(\d{2})/.exec(String(iso || ""));
  if (!m) return String(iso || "") || "—";
  return `${MONTHS[Number(m[2]) - 1] ?? m[2]}${m[1]}`;
}

/** The fields of a screener row that these helpers read. Structurally compatible with ScreenView's Purchase. */
export type ScreenerRowLike = {
  conid: string | number;
  symbol?: string | null;
  expiry: string;
  right?: string | null;
  strike: number;
  dte: number;
  bid?: number | null;
  ask?: number | null;
  mid?: number | null;
  midPrice?: number | null;
  vega?: number | null;
  [key: string]: any;
};

/** Round-trip quoted cost as a share of premium: (ask - bid) / mid. Null when the quote is incomplete. */
export function roundTripSpreadPct(p: ScreenerRowLike): number | null {
  const bid = typeof p.bid === "number" ? p.bid : null;
  const ask = typeof p.ask === "number" ? p.ask : null;
  const mid = typeof p.mid === "number" ? p.mid : typeof p.midPrice === "number" ? p.midPrice : null;
  if (bid == null || ask == null || mid == null || mid <= 0 || ask < bid) return null;
  return (ask - bid) / mid;
}

/** Dollars of vega per $1,000 of premium = (vega / mid) * 1000. */
export function vegaPer1k(p: ScreenerRowLike): number | null {
  const vega = typeof p.vega === "number" ? p.vega : null;
  const mid = typeof p.mid === "number" ? p.mid : typeof p.midPrice === "number" ? p.midPrice : null;
  if (vega == null || mid == null || mid <= 0) return null;
  return (vega / mid) * 1000;
}

const firstNum = (...values: any[]): number | null => {
  for (const v of values) if (typeof v === "number" && Number.isFinite(v)) return v;
  return null;
};

/* ---------------------------------------------------------------------------------------------
 * Fix 1 (P0, finding 1): the own-tenor valuation shadow.
 *
 * The backend writes it at meta.diagnostics.tenor_valuation_shadow (screener_core, commit 633d670a). The page read
 * meta.tenor_valuation_shadow, so the warning never rendered: on 2026-10-27 the page called every contract "cheap"
 * while DEC2030 read rich (89th pct) at its own tenor.
 * ------------------------------------------------------------------------------------------- */

export type TenorShadowExample = {
  dte?: number | null;
  bucket?: string | null;
  live_percentile?: number | null;
  live_label?: string | null;
  shadow_percentile?: number | null;
  shadow_label?: string | null;
};

export type TenorShadow = {
  enabled?: boolean | null;
  evaluated?: number | null;
  value_label_changed?: number | null;
  value_label_changed_pct?: number | null;
  mean_percentile_delta?: number | null;
  long_end_evaluated?: number | null;
  long_end_mean_percentile_delta?: number | null;
  examples?: TenorShadowExample[] | null;
  note?: string | null;
};

/** diagnostics first (where the backend writes it), then meta (older/lifted payloads). */
export function readTenorShadow(meta: any): TenorShadow | null {
  const fromDiagnostics = meta?.diagnostics?.tenor_valuation_shadow;
  if (fromDiagnostics && typeof fromDiagnostics === "object") return fromDiagnostics as TenorShadow;
  const fromMeta = meta?.tenor_valuation_shadow;
  if (fromMeta && typeof fromMeta === "object") return fromMeta as TenorShadow;
  return null;
}

const labelWords = (label?: string | null) => String(label || "").replaceAll("_", " ").trim();
const ordinal = (n: number) => {
  const v = Math.round(n);
  const s = ["th", "st", "nd", "rd"];
  const r = v % 100;
  return `${v}${s[(r - 20) % 10] || s[r] || s[0]}`;
};

export type TenorShadowRead = {
  show: boolean;
  /** true when the shadow would change what the page says (a relabel, or a long end >= 5 pctl richer). */
  warn: boolean;
  headline: string;
  examples: { expiry: string; text: string }[];
  tooltip: string;
};

/**
 * Plain-English read of the shadow. `rows` maps a DTE back to its expiry (the shadow examples carry DTE only).
 * Positive long_end_mean_percentile_delta = own-tenor percentile HIGHER = RICHER than the 3y-node proxy.
 */
export function describeTenorShadow(
  shadow: TenorShadow | null | undefined,
  rows: { dte?: number | null; expiry?: string | null }[] = [],
): TenorShadowRead {
  const empty: TenorShadowRead = { show: false, warn: false, headline: "", examples: [], tooltip: "" };
  if (!shadow || !shadow.enabled) return empty;
  const evaluated = Number(shadow.evaluated ?? 0);
  if (!(evaluated > 0)) return empty;

  const changed = Number(shadow.value_label_changed ?? 0);
  const longDelta = firstNum(shadow.long_end_mean_percentile_delta);
  const dteToExpiry = new Map<number, string>();
  rows.forEach((r) => {
    if (typeof r.dte === "number" && r.expiry && !dteToExpiry.has(r.dte)) dteToExpiry.set(r.dte, String(r.expiry));
  });

  const seen = new Set<string>();
  const examples: { expiry: string; text: string }[] = [];
  (shadow.examples || []).forEach((ex) => {
    const exp = typeof ex.dte === "number" && dteToExpiry.has(ex.dte) ? expiryMonLabel(dteToExpiry.get(ex.dte)) : `DTE ${ex.dte ?? "?"}`;
    const lp = firstNum(ex.live_percentile);
    const sp = firstNum(ex.shadow_percentile);
    const text =
      `${exp}: ${labelWords(ex.live_label) || "—"} ${lp != null ? ordinal(lp) : "—"}` +
      ` → ${labelWords(ex.shadow_label) || "—"} ${sp != null ? ordinal(sp) : "—"} pct`;
    if (seen.has(text)) return;
    seen.add(text);
    examples.push({ expiry: exp, text });
  });

  const liveLabels = (shadow.examples || []).map((e) => labelWords(e.live_label)).filter(Boolean);
  const proxyRead = liveLabels.length ? liveLabels[0] : null;
  const parts: string[] = [];
  if (longDelta != null && Math.abs(longDelta) >= 0.05) {
    const dir = longDelta > 0 ? "richer" : "cheaper";
    parts.push(
      `Long end reads ${proxyRead ?? "as shown"} on the 3-year proxy but ${numberFmt(Math.abs(longDelta), 1)} percentile points ${dir} at its own tenor`,
    );
  } else if (longDelta != null) {
    parts.push("Long end reads the same at its own tenor as on the 3-year proxy");
  }
  parts.push(
    changed > 0
      ? `${changed} of ${evaluated} contracts would change label`
      : `${evaluated} contracts checked, none would change label`,
  );
  let headline = parts.join("; ");
  if (examples.length) headline += ` (e.g. ${examples[0].text})`;

  const tooltip =
    "SHADOW ONLY — does not change the ranking, the Priority order or any size.\n\n" +
    "Every 30m+ contract is valued at the SAME 3-year surface node (the proxy). This re-values each contract at its OWN tenor.\n" +
    `${evaluated} contract(s) evaluated, ${shadow.long_end_evaluated ?? 0} of them 30m+. ` +
    `${changed} would change value label (${shadow.value_label_changed_pct ?? 0}%).\n` +
    `Mean percentile shift: ${shadow.mean_percentile_delta ?? "—"}` +
    (longDelta != null ? `; long end ${longDelta > 0 ? "+" : ""}${longDelta}` : "") +
    ".\n\nPOSITIVE means the own-tenor percentile is HIGHER, i.e. the contract is RICHER at its own tenor than the " +
    "3-year proxy says (the proxy flatters it). Negative means cheaper.";

  return {
    show: true,
    warn: changed > 0 || (longDelta != null && longDelta >= 5),
    headline,
    examples,
    tooltip,
  };
}

/**
 * The backend's market-opportunity detail says "cheapest tenor percentile X" without saying which tenor. When that
 * tenor is 30m+, X is a 3-year-node proxy (every 30m+ contract is valued at the same 3y node); say so.
 */
export function annotateOpportunityDetail(
  detail: string | null | undefined,
  termPercentiles: Record<string, number | null | undefined> | null | undefined,
): string | null {
  if (!detail) return detail ?? null;
  const entries = Object.entries(termPercentiles || {}).filter(
    (e): e is [string, number] => typeof e[1] === "number" && Number.isFinite(e[1]),
  );
  if (!entries.length) return detail;
  const [bucket] = entries.reduce((a, b) => (b[1] < a[1] ? b : a));
  if (bucket !== "30m_plus") return detail;
  return detail.replace(/(cheapest tenor percentile [\d.]+)/, "$1 (30m+, 3y-node proxy)");
}

/* ---------------------------------------------------------------------------------------------
 * Fix 2 (finding 3): already-queued tickets.
 * ------------------------------------------------------------------------------------------- */

export type TicketStateLike = {
  status: "queued" | "staged" | "duplicate";
  orderId?: number | null;
  label?: string | null;
};

/**
 * Re-read pending BUY tickets whenever the run produced candidate rows. The old gate was data.purchases, which the
 * allocator fills and which is ALWAYS empty in market-rank mode, so the read never happened.
 */
export function shouldHydrateQueued(candidateRowCount: number): boolean {
  return candidateRowCount > 0;
}

/**
 * Phase 2 (CIO 2026-10-28): in market-rank mode screener_core sends the same whyIncluded sentence on every row, which
 * made each table row three lines tall. The table hides the standard sentence; any other reason still shows. The
 * Excel export keeps the full text.
 */
const STANDARD_RANK_REASONS = new Set([
  "Passed Helion filters and was ranked by vega efficiency, surface value, and tenor.",
  "Ranked market screen; no sizing/allocation applied.",
]);

export function rowReasonToShow(reason: string | null | undefined): string | null {
  const text = String(reason || "").trim();
  return text && !STANDARD_RANK_REASONS.has(text) ? text : null;
}

export function deskLabelFor(orderId: number | null | undefined): string {
  return typeof orderId === "number" && Number.isFinite(orderId) ? `Queued #${orderId}` : "Queued";
}

/** get_pending_orders payload -> conid -> ticket state (BUY tickets only). */
export function parsePendingBuys(payload: any): Record<string, TicketStateLike> {
  const rows = Array.isArray(payload?.pendingOrders) ? payload.pendingOrders : [];
  const next: Record<string, TicketStateLike> = {};
  rows.forEach((row: any) => {
    const action = String(row?.action || row?.Action || "").toUpperCase();
    const contractId = String(row?.contractId ?? row?.ContractId ?? "").trim();
    if (action !== "BUY" || !contractId) return;
    const oid = Number(row?.orderId ?? row?.OrderId ?? NaN);
    const orderId = Number.isFinite(oid) ? oid : null;
    next[contractId] = { status: "queued", orderId, label: deskLabelFor(orderId) };
  });
  return next;
}

/**
 * Merge a fresh pending-ticket read with what this session already knows. The server list is authoritative for
 * hydrated rows (a ticket approved or rejected since disappears); a ticket THIS session staged (or was told is a
 * duplicate) is kept, so a hydration racing a queue click cannot erase it.
 */
export function mergeTicketStates(
  server: Record<string, TicketStateLike>,
  prev: Record<string, TicketStateLike>,
): Record<string, TicketStateLike> {
  const out: Record<string, TicketStateLike> = { ...server };
  Object.entries(prev).forEach(([k, v]) => {
    if (v.status === "staged" || v.status === "duplicate") out[k] = { ...server[k], ...v };
  });
  return out;
}

/** What the Desk column says for a row: the ticket when there is one, otherwise "Ranked". */
export function deskCell(ticket: TicketStateLike | null | undefined): { kind: "ticket" | "ranked"; text: string } {
  if (ticket) return { kind: "ticket", text: deskLabelFor(ticket.orderId) };
  return { kind: "ranked", text: "Ranked" };
}

/* ---------------------------------------------------------------------------------------------
 * Fix 3 (finding 4): Unknown, not Inactive.
 * ------------------------------------------------------------------------------------------- */

export type ContextLoadStatus = "loading" | "loaded" | "failed";

export function defenseOverlayDisplay(args: {
  status: ContextLoadStatus;
  regimeContextSource?: string | null;
  defenseOverlayActive?: boolean | null;
  defenseOverlayState?: string | null;
}): { text: string; known: boolean; note: string | null } {
  if (args.status === "loading") return { text: "—", known: false, note: "Loading risk context…" };
  if (args.status === "failed") return { text: "Unknown", known: false, note: "Risk context unavailable" };
  if (String(args.regimeContextSource || "").toLowerCase() === "fallback") {
    return { text: "Unknown", known: false, note: "Engine not consulted (risk_kpis fallback)" };
  }
  if (args.defenseOverlayActive == null) return { text: "Unknown", known: false, note: "Not reported" };
  if (args.defenseOverlayActive) return { text: args.defenseOverlayState || "Active", known: true, note: null };
  return { text: "Inactive", known: true, note: null };
}

export function effectiveTargetCaption(status: ContextLoadStatus, regimeContextSource?: string | null): string {
  if (status === "failed") return "Risk context unavailable";
  if (String(regimeContextSource || "").toLowerCase() === "fallback") {
    return "Base band target — engine not consulted, so defense / grind are not reflected";
  }
  return "Engine aim today — falls below the cap under defense, not a breach line";
}

/**
 * The context as-of line (fixed 2026-10-28 after the first live run). risk_kpis sends the NAV time as a naive UTC
 * isoformat ("2026-10-28T15:47:23") and the VIX close as a date only ("2026-10-27"). `new Date()` read the first as
 * local time (NAV "3:47 PM" at a 9:47 AM MT run) and the second as UTC midnight (VIX "10/26, 6:00 PM").
 */
export function contextAsOfText(navAsOf?: string | null, vixAsOf?: string | null): string {
  return `NAV as of ${navAsOfPart(navAsOf)} · VIX as of ${vixAsOfPart(vixAsOf)}`;
}

/** The NAV half of contextAsOfText ("10/27/2026, 9:00:00 AM" or "not reported"). Phase 2: per-tile as-of. */
export function navAsOfPart(navAsOf?: string | null): string {
  const s = String(navAsOf || "").trim();
  if (!s) return "not reported";
  const iso = /^\d{4}-\d{2}-\d{2}[T ]\d{2}:\d{2}(:\d{2}(\.\d+)?)?$/.test(s) ? `${s.replace(" ", "T")}Z` : s;
  const d = new Date(iso);
  return Number.isFinite(d.getTime()) ? d.toLocaleString() : s;
}

/** The VIX half of contextAsOfText ("10/26/2026 close" or "not reported"). */
export function vixAsOfPart(vixAsOf?: string | null): string {
  const s = String(vixAsOf || "").trim();
  if (!s) return "not reported";
  const m = /^(\d{4})-(\d{2})-(\d{2})$/.exec(s);
  if (m) return `${Number(m[2])}/${Number(m[3])}/${m[1]} close`;
  const d = new Date(s);
  return Number.isFinite(d.getTime()) ? d.toLocaleString() : s;
}

/* ---------------------------------------------------------------------------------------------
 * Phase 2 (layout, audit 2026-10-27): one diagnostics drawer with ONE status, which opens itself on any non-pass
 * state. The states are listed here, pure, so the drawer can never say "all pass" over a state the page knows is not.
 * ------------------------------------------------------------------------------------------- */

export type DiagnosticIssue = { key: string; level: "warn" | "fail"; text: string };

export function diagnosticsIssues(args: {
  dataConfidenceStatus?: string | null;
  decisionContextFailed?: boolean;
  hardBlockCount?: number;
  invariantViolations?: number;
  quotesResolved: number;
  quotesIncomplete: number;
  quoteTone?: string | null;
  integrityRows?: { label: string; tone: string; status: string }[];
  riskContextFailed?: boolean;
  contextErrors?: string[];
  runArchiveStatus?: string | null;
  pendingReadFailed?: boolean;
}): DiagnosticIssue[] {
  const out: DiagnosticIssue[] = [];
  const dc = String(args.dataConfidenceStatus || "").toLowerCase();
  if (dc === "fail" || dc === "halt" || dc === "error") out.push({ key: "data", level: "fail", text: "Data confidence FAIL" });
  else if (dc === "warn" || dc === "warning") out.push({ key: "data", level: "warn", text: "Data confidence WARN" });
  else if (dc !== "pass" && dc !== "ok") out.push({ key: "data", level: "warn", text: "Data confidence not reported" });
  if (args.decisionContextFailed) out.push({ key: "context", level: "fail", text: "Decision context FAIL" });
  if ((args.hardBlockCount || 0) > 0) out.push({ key: "hard", level: "fail", text: `${args.hardBlockCount} hard block${args.hardBlockCount === 1 ? "" : "s"}` });
  if ((args.invariantViolations || 0) > 0) {
    out.push({ key: "invariant", level: "fail", text: `${args.invariantViolations} invariant violation${args.invariantViolations === 1 ? "" : "s"}` });
  }
  if (!(args.quotesResolved > 0)) {
    out.push({ key: "quotes", level: "warn", text: "Quote hydration not reported" });
  } else if (args.quotesIncomplete > 0 || args.quoteTone === "warning" || args.quoteTone === "danger") {
    out.push({
      key: "quotes",
      level: args.quoteTone === "danger" ? "fail" : "warn",
      text: `Quotes missing: ${args.quotesIncomplete} of ${args.quotesResolved} incomplete`,
    });
  }
  (args.integrityRows || []).forEach((r) => {
    if (r.tone === "warning" || r.tone === "danger") {
      out.push({ key: `integrity:${r.label}`, level: r.tone === "danger" ? "fail" : "warn", text: `${r.label}: ${r.status}` });
    }
  });
  if (args.riskContextFailed || (args.contextErrors || []).length) {
    out.push({ key: "risk", level: "warn", text: "Risk context unavailable" });
  }
  if (args.runArchiveStatus === "failed") out.push({ key: "archive", level: "fail", text: "Run NOT saved" });
  if (args.pendingReadFailed) out.push({ key: "pending", level: "warn", text: "Pending tickets not re-read" });
  return out;
}

export function diagnosticsHeadline(issues: DiagnosticIssue[]): { label: string; detail: string; tone: "pass" | "warn" | "fail" } {
  const detail = issues.map((i) => i.text).join(" · ");
  if (issues.some((i) => i.level === "fail")) return { label: "failure", detail, tone: "fail" };
  if (issues.length) return { label: `${issues.length} warning${issues.length === 1 ? "" : "s"}`, detail, tone: "warn" };
  return { label: "all pass", detail: "", tone: "pass" };
}

/** "N strikes qualify" per rung tile: the ranked rows on each expiry (Phase 2, replaces the Ladder by Expiration card). */
export function strikesByExpiry(rows: { expiry?: string | null }[] | null | undefined): Record<string, number> {
  const out: Record<string, number> = {};
  (rows || []).forEach((r) => {
    const k = String(r.expiry || "");
    if (k) out[k] = (out[k] || 0) + 1;
  });
  return out;
}

/**
 * Sum of vega x qty x multiplier over the held LEAPS rows. A missing vega is NOT zero: those lines are counted and
 * reported, and when every line is missing (or there are no rows) the total is null ("—"), never $0.
 */
export function portfolioVegaFromRows(
  rows: { Vega?: any; Quantity?: any; Multiplier?: any }[] | null | undefined,
): { total: number | null; lines: number; missingVega: number } {
  const list = Array.isArray(rows) ? rows : [];
  let total = 0;
  let known = 0;
  let missing = 0;
  list.forEach((r) => {
    const vega = r?.Vega;
    const qty = Number(r?.Quantity);
    const mult = r?.Multiplier == null || !Number.isFinite(Number(r.Multiplier)) ? 100 : Number(r.Multiplier);
    if (vega == null || vega === "" || !Number.isFinite(Number(vega)) || !Number.isFinite(qty)) {
      missing += 1;
      return;
    }
    total += Number(vega) * qty * mult;
    known += 1;
  });
  return { total: known > 0 && Number.isFinite(total) ? total : null, lines: list.length, missingVega: missing };
}

export function portfolioVegaText(v: { total: number | null; lines: number; missingVega: number } | null): string {
  if (!v || v.total == null) return "—";
  return v.missingVega > 0
    ? `${currency(v.total)} (${v.missingVega} line${v.missingVega === 1 ? "" : "s"} missing vega)`
    : currency(v.total);
}

/* ---------------------------------------------------------------------------------------------
 * Fix 4 (finding 5): config defaults start blank and send null.
 * ------------------------------------------------------------------------------------------- */

export type ConfigOverrideForm = {
  bandPct: string;
  moneynessBufferPct: string;
  expirationCapPct: string;
  vegaExpCapPct: string;
  minDistinctExpiries: string;
  lineShareCapPct: string;
  maxTranchesTotal: string;
  maxTranchesPerExp: string;
  minQtyNewLine: string;
  blockQtyExisting: string;
  /** "" = config default, "true" / "false" = override */
  fillerEnabled: string;
  /** "" = config default */
  fillerMode: string;
  finalTopup: string;
  finalTopupMaxUnits: string;
};

export const BLANK_CONFIG_FORM: ConfigOverrideForm = {
  bandPct: "",
  moneynessBufferPct: "",
  expirationCapPct: "",
  vegaExpCapPct: "",
  minDistinctExpiries: "",
  lineShareCapPct: "",
  maxTranchesTotal: "",
  maxTranchesPerExp: "",
  minQtyNewLine: "",
  blockQtyExisting: "",
  fillerEnabled: "",
  fillerMode: "",
  finalTopup: "",
  finalTopupMaxUnits: "",
};

const numOrNull = (s: string): number | null => {
  if (s == null || String(s).trim() === "") return null;
  const n = Number(s);
  return Number.isFinite(n) ? n : null;
};
const boolOrNull = (s: string): boolean | null => (s === "true" ? true : s === "false" ? false : null);

/**
 * The request fields for the config-backed knobs. Blank = null = entry_signals.json / env decides (the 2026-09-10
 * Step 1 rule, extended to the 14 controls it missed). quote_hydration_* are always null here; Rehydrate Quotes adds
 * its own extra-retry count as a per-click override.
 *
 * max_tranches_* are OMITTED (undefined) when blank rather than sent as null: the handler passes the key straight
 * to run_screener_input_only, whose `int(max_tranches_total or 0)` turns an explicit null into 0, while an absent key
 * keeps the core's own default (3 / 5) -- the value the live run used.
 */
export function buildConfigOverrideFields(form: ConfigOverrideForm): Record<string, any> {
  const out: Record<string, any> = {
    moneyness_band_pct: numOrNull(form.bandPct),
    moneyness_band_buffer_pct: numOrNull(form.moneynessBufferPct),
    expiration_cap_pct: numOrNull(form.expirationCapPct),
    vega_exp_cap_pct: numOrNull(form.vegaExpCapPct),
    min_distinct_expiries: numOrNull(form.minDistinctExpiries),
    line_share_cap_pct: numOrNull(form.lineShareCapPct),
    min_qty_per_new_line: numOrNull(form.minQtyNewLine),
    block_qty_existing: numOrNull(form.blockQtyExisting),
    filler_enabled: boolOrNull(form.fillerEnabled),
    filler_mode: form.fillerMode === "smart_topup" || form.fillerMode === "original_singletons" ? form.fillerMode : null,
    final_topup: boolOrNull(form.finalTopup),
    final_topup_max_units: numOrNull(form.finalTopupMaxUnits),
    quote_hydration_max_attempts: null,
    quote_hydration_warmup_ms: null,
    quote_hydration_retry_sleep_ms: null,
    quote_hydration_singleton_limit: null,
    quote_hydration_singleton_sleep_ms: null,
  };
  const mt = numOrNull(form.maxTranchesTotal);
  const mpe = numOrNull(form.maxTranchesPerExp);
  if (mt != null) out.max_tranches_total = mt;
  if (mpe != null) out.max_tranches_per_expiration = mpe;
  return out;
}

/** The value the backend actually used, read back from meta.inputs (flat keys first, then the nested config blocks). */
export function resolvedConfigValues(inputs: Record<string, any> | null | undefined): Record<keyof ConfigOverrideForm, any> {
  const i = inputs || {};
  const filler = (i.filler || {}) as Record<string, any>;
  const within = (i.within_expiry || {}) as Record<string, any>;
  const pick = (...v: any[]) => {
    for (const x of v) if (x !== undefined && x !== null && x !== "") return x;
    return null;
  };
  return {
    bandPct: pick(i.moneyness_band_pct, i.moneyness_band),
    moneynessBufferPct: pick(i.moneyness_band_buffer_pct),
    expirationCapPct: pick(i.expiration_cap_pct),
    vegaExpCapPct: pick(i.vega_exp_cap_pct),
    minDistinctExpiries: pick(i.ladder_min_distinct_expiries, i.min_distinct_expiries),
    lineShareCapPct: pick(i.line_share_cap_pct, within.line_share_cap_pct),
    maxTranchesTotal: pick(i.max_tranches_total),
    maxTranchesPerExp: pick(i.max_tranches_per_expiration),
    minQtyNewLine: pick(i.min_qty_per_new_line, filler.min_qty_per_new_line),
    blockQtyExisting: pick(i.block_qty_existing, filler.block_qty_existing),
    fillerEnabled: pick(i.filler_enabled, filler.enabled),
    fillerMode: pick(i.filler_mode, filler.mode),
    finalTopup: pick(i.final_topup, filler.final_topup),
    finalTopupMaxUnits: pick(i.final_topup_max_units, filler.final_topup_max_units),
  };
}

export function configPlaceholder(value: any): string {
  if (value === null || value === undefined || value === "") return "config default";
  return `config: ${String(value)}`;
}

/** The ladder-breadth target the run used. The backend publishes ladder_min_distinct_expiries, never min_distinct_expiries. */
export function resolveMinDistinctExpiries(inputs: Record<string, any> | null | undefined): number | null {
  return firstNum(inputs?.ladder_min_distinct_expiries, inputs?.min_distinct_expiries);
}

/* ---------------------------------------------------------------------------------------------
 * Fix 5 (CIO decision 2): the engine's resolver decides the mode.
 * ------------------------------------------------------------------------------------------- */

export type RegimeFieldsSource = {
  baseRegimeBand?: string | null;
  engineEffectiveRegime?: string | null;
  defenseOverlayState?: string | null;
  defenseOverlayActive?: boolean | null;
  bestDeploymentBucket?: string | null;
  surfaceAlignmentPct?: number | null;
};

/**
 * deployment_mode is ALWAYS null: entry_ranking.resolve_deployment_mode decides from the raw regime fields
 * (defense / panic / high-vol -> defensive; alignment < 0.45 -> selective; 24-30m best bucket -> aggressive; else
 * normal). surface_alignment_pct is the surface_overview score, a FRACTION 0-1 (the page renders 0.57 as "57%"),
 * which is the unit the resolver's 0.45 threshold expects. effective_regime is the engine's / risk_kpis' own regime
 * string only -- the surface "stance" (constructive / patient ...) is not a regime and is no longer substituted.
 */
export function buildRegimeRequestFields(ctx: RegimeFieldsSource | null, status: ContextLoadStatus): Record<string, any> {
  const loaded = status === "loaded" && ctx != null;
  return {
    deployment_mode: null,
    base_regime_band: ctx?.baseRegimeBand ?? null,
    effective_regime: ctx?.engineEffectiveRegime ?? null,
    defense_overlay_state: ctx?.defenseOverlayState ?? null,
    defense_overlay_active: loaded ? Boolean(ctx?.defenseOverlayActive) : null,
    best_deployment_bucket: ctx?.bestDeploymentBucket ?? null,
    surface_alignment_pct: ctx?.surfaceAlignmentPct ?? null,
  };
}

/** The mode/regime/defense the RUN used, from the backend's echo in meta.inputs.risk_context. */
export function runUsedContext(
  riskContextEcho: Record<string, any> | null | undefined,
  pageDefenseKnown: boolean,
): { mode: string | null; line: string | null } {
  const rc = riskContextEcho || null;
  if (!rc) return { mode: null, line: null };
  const mode = typeof rc.deployment_mode === "string" && rc.deployment_mode ? rc.deployment_mode : null;
  const regime = rc.effective_regime || rc.base_regime_band || "not sent";
  const defense = rc.defense_overlay_active
    ? `active${rc.defense_overlay_state ? ` (${rc.defense_overlay_state})` : ""}`
    : pageDefenseKnown
    ? "inactive"
    : "unknown (run treated it as off)";
  return { mode, line: `This run used: mode ${mode ?? "not reported"} · regime ${regime} · defense ${defense}` };
}

/* ---------------------------------------------------------------------------------------------
 * Fix 6 (finding 6): Priority explainer driven by the ladder payload.
 * ------------------------------------------------------------------------------------------- */

export type LadderPriorityLike = {
  active?: boolean | null;
  note?: string | null;
  rule?: string | null;
  rungs?: { expiry: string; rollingOff?: boolean | null; status?: string | null }[] | null;
};

export function priorityToggleLabel(ladder: LadderPriorityLike | null | undefined): string {
  return ladder?.active ? "Priority (ladder first)" : "Priority (tenor first)";
}

export function priorityExplainer(ladder: LadderPriorityLike | null | undefined): string {
  if (!ladder?.active) {
    const why = ladder?.note
      ? ladder.note
      : ladder
      ? "the held ladder is switched off for this run"
      : "this backend did not publish a ladder";
    return (
      `Priority (tenor first): the held ladder is not used for this run (${why.replace(/\.$/, "")}), so the order is ` +
      "tenor priority (24–30m first, then the nearest beyond), then Entry Score. Display order only."
    );
  }
  const rule = String(ladder.rule || "Priority = rung status against the ladder target at cost, then tenor, then Entry Score.").trim();
  const mentionsRollOff = /18 months|roll/i.test(rule);
  const rolling = (ladder.rungs || []).filter((r) => r.rollingOff).map((r) => expiryMonLabel(r.expiry));
  const roll = mentionsRollOff
    ? ""
    : " A rung that drops below 18 months within 90 days ranks after the on-target rungs, even with room" +
      (rolling.length ? ` (today: ${rolling.join(", ")})` : "") +
      ".";
  return `Priority order (ladder first): ${rule}${roll}`;
}

/* ---------------------------------------------------------------------------------------------
 * Fix 8 (finding 8): ticket audit text.
 * ------------------------------------------------------------------------------------------- */

/** The signed-in identity, or null. Never a placeholder: a ticket must be attributable to a real user. */
export function requesterIdentity(userEmail: string | null | undefined): string | null {
  const s = String(userEmail ?? "").trim();
  return s ? s : null;
}

export const REQUESTER_MISSING_BLOCKER = "Signed-in user not loaded — a ticket cannot be attributed, so queueing is blocked";

export function allocatorReasonText(marketRankOnly: boolean, skipReason: string | null | undefined): string {
  if (marketRankOnly) return "allocator: not run (market rank)";
  if (!skipReason) return "allocator: Selected";
  return `allocator: ${titleizeCode(skipReason)}`;
}

export function buildTicketReasonText(
  p: ScreenerRowLike & { tenorBucket?: string | null; entryScore?: number | null; surfaceScore?: number | null; skipReason?: string | null },
  opts: { cioSized: boolean; marketRankOnly: boolean },
): string {
  const strike = Number.isFinite(p.strike) ? numberFmt(p.strike, 0) : String(p.strike);
  const scoreBits = [
    p.tenorBucket ? `tenor=${p.tenorBucket}` : null,
    typeof p.entryScore === "number" ? `entry=${p.entryScore.toFixed(3)}` : null,
    typeof p.surfaceScore === "number" ? `surface=${p.surfaceScore.toFixed(3)}` : null,
  ].filter(Boolean);
  return [
    opts.cioSized ? "cio_sized_buy" : "screener_manual_buy",
    `${p.symbol} ${p.expiry} ${p.right}${strike}`,
    opts.cioSized ? `qty set by CIO (${allocatorReasonText(opts.marketRankOnly, p.skipReason)})` : null,
    scoreBits.join(" | "),
  ]
    .filter(Boolean)
    .join(" | ");
}

/**
 * Basis-consistent allocation tags plus the Priority context the CIO acted on. The old pair put a cost-basis
 * target beside a MARKET-value weight (the pairing the 2026-08-13 fix corrected for targetAllocation /
 * currentAllocation but missed in the tags).
 */
export function ticketContextTags(args: {
  basisTargetLeapsPct?: number | null;
  targetLeapsPct?: number | null;
  basisLeapsAllocationPct?: number | null;
  currentLeapsAllocationPct?: number | null;
  priorityRank?: number | null;
  rungStatus?: string | null;
  ladderPriorityActive?: boolean | null;
}): Record<string, any> {
  return {
    allocationBasis: "funded_cost",
    targetLeapsBasisPct: args.basisTargetLeapsPct ?? args.targetLeapsPct ?? null,
    currentLeapsBasisPct: args.basisLeapsAllocationPct ?? null,
    currentLeapsMarketValuePct: args.currentLeapsAllocationPct ?? null,
    priorityRank: typeof args.priorityRank === "number" ? args.priorityRank : null,
    rungStatus: args.rungStatus ?? null,
    ladderPriorityActive: args.ladderPriorityActive == null ? null : Boolean(args.ladderPriorityActive),
  };
}

/* ---------------------------------------------------------------------------------------------
 * Fix 9 (finding 10): the ML false alarm.
 * ------------------------------------------------------------------------------------------- */

const ML_FAILED = "ml_scoring_failed_scores_deflated";

/** The ML weight the run used: meta.inputs.surface_ranking.score_weights.ml, else meta.inputs.ml.weight_configured. */
export function mlWeightFromInputs(inputs: Record<string, any> | null | undefined): number | null {
  return firstNum(inputs?.surface_ranking?.score_weights?.ml, inputs?.ml?.weight_configured);
}

/**
 * Render the warning list without hiding anything (+N more is counted, never dropped). Unknown codes (e.g. the new
 * quotes_missing) render as text. "ML scoring failed - scores deflated" is hoisted first ONLY when the ML term
 * actually carries weight; at weight 0 it changes no score, so it goes last and says so.
 */
export function summarizeDataWarnings(warnings: string[], opts: { mlWeight?: number | null } = {}): string {
  const list = (warnings || []).filter((w) => typeof w === "string" && w.trim());
  const mlInert = opts.mlWeight === 0;
  const critical = mlInert ? [] : list.filter((w) => w === ML_FAILED);
  const rest = list.filter((w) => w !== ML_FAILED);
  const trailing = mlInert ? list.filter((w) => w === ML_FAILED) : [];
  const ordered = [...critical, ...rest, ...trailing];
  const label = (w: string) => (w === ML_FAILED && mlInert ? "ML scoring failed (ML weight 0: no score effect)" : titleizeCode(w));
  const shown = ordered.slice(0, 2).map(label).join("; ");
  const hidden = ordered.length - Math.min(2, ordered.length);
  return hidden > 0 ? `${shown} · +${hidden} more` : shown;
}

/* ---------------------------------------------------------------------------------------------
 * Fix 11 (CIO decision 4): Contract Quality is tenor-driven.
 * ------------------------------------------------------------------------------------------- */

export const TENOR_DRIVEN_GRADE_TOOLTIP =
  "Tenor-driven: long-dated Entry Scores carry a 1 + DTE/1000 tilt, so on any day with long-dated boards the " +
  "universe grade saturates at A. It is not a quality read; size on the drivers, not the letter.";

export function gradeWithTenorNote(grade: string | null | undefined): string {
  return grade ? `${grade} (tenor-driven)` : "—";
}

/* ---------------------------------------------------------------------------------------------
 * Fix 10 (finding 9): the Excel export.
 * ------------------------------------------------------------------------------------------- */

export const FULL_UNIVERSE_HEADERS = [
  "Provisional Rank", "Full Score Rank", "Eligibility", "Quote Status", "Hydration Status", "Attempts",
  "Returned Fields", "Missing Fields", "Quote Reason", "Expiry", "Right", "Strike", "DTE", "Moneyness",
  "Band Status", "Conid", "Bid", "Ask", "Mid", "Vega", "OI", "OI Status", "Liquidity", "Sizing Max",
  "Surface Score", "Provisional Universe Score", "Value", "Tenor",
];

const listText = (v: any) => (Array.isArray(v) ? v.join(", ") : v == null ? "" : String(v));

/** One Full-Universe row, cell-for-cell with FULL_UNIVERSE_HEADERS (the old builder had 24 cells under 28 headers). */
export function fullUniverseCells(p: ScreenerRowLike): string[] {
  return [
    numberFmt(p.provisionalUniverseRank ?? p.postOiUniverseRank ?? null, 0),
    p.fullScoreRank == null ? "—" : numberFmt(p.fullScoreRank, 0),
    titleizeCode(p.rankEligibility),
    titleizeCode(p.quoteStatus || "QUOTE_OK"),
    titleizeCode(p.quoteHydrationStatus),
    p.quoteHydrationAttempts == null ? "—" : String(p.quoteHydrationAttempts),
    listText(p.snapshotReturnedFields),
    listText(p.snapshotMissingRequestedFields),
    p.quoteReason || p.skipReasonDetail || "",
    p.expiry,
    String(p.right ?? ""),
    numberFmt(p.strike, 0),
    String(p.dte),
    pctFmt(p.moneyness ?? null, 1),
    titleizeCode(p.moneynessBandStatus),
    String(p.conid),
    numberFmt(p.bid ?? null, 2),
    numberFmt(p.ask ?? null, 2),
    numberFmt(p.mid ?? p.midPrice ?? null, 2),
    numberFmt(p.vega ?? null, 3),
    p.openInterestStatus === "unavailable_verify" ? "Verify" : numberFmt(p.openInterest ?? null, 0),
    p.openInterestStatus || "known",
    titleizeCode(p.liquidityTier),
    numberFmt(p.sizingMaxInitialQty ?? p.oiCapQty ?? null, 0),
    numberFmt(p.surfaceScore ?? null, 3),
    numberFmt(p.provisionalUniverseScore ?? null, 3),
    p.surfaceAbsoluteValueLabel ?? p.surfaceValueLabel ?? "",
    titleizeBucket(p.tenorBucket),
  ];
}

export const CONTRACTS_HEADERS = [
  "Priority", "Rung", "Decision", "Reason", "Grade", "Entry Score", "Spread %", "Vega/$1k", "Surface Score",
  "Surface Raw", "Absolute Value", "Relative Value", "Term Percentile", "Shape Label", "Tenor Bucket", "Expiry",
  "Right", "Strike", "DTE", "Moneyness", "Band Status", "Conid", "Bid", "Ask", "Mid", "Unit Cost", "Vega",
  "Open Interest", "OI Status", "Liquidity Tier", "Sizing Max", "Sizing Note", "Qty", "Est Notional", "Est Vega USD/pt",
];

export function rungLabelFor(
  status: string | null | undefined,
  rollingOff: boolean | null | undefined,
): string {
  if (!status) return "";
  const base = ({ room: "Room", on_target: "On target", full: "Full", outside_ladder: "Outside ladder" } as Record<string, string>)[status] ?? titleizeCode(status);
  return rollingOff ? `${base} · rolling off` : base;
}

export function contractsCells(
  p: ScreenerRowLike,
  ctx: { decision: string; reason: string; marketRankOnly: boolean; rollingOff?: boolean | null },
): string[] {
  const spread = roundTripSpreadPct(p);
  const vpk = vegaPer1k(p);
  return [
    typeof p.priorityRank === "number" ? String(p.priorityRank) : "—",
    rungLabelFor(p.rungStatus, ctx.rollingOff),
    ctx.decision,
    ctx.reason,
    p.entryGrade ?? "",
    numberFmt(p.entryScore ?? null, 3),
    spread == null ? "—" : pctFmt(spread, 1),
    vpk == null ? "—" : numberFmt(vpk, 1),
    numberFmt(p.surfaceScore ?? null, 3),
    numberFmt(p.surfaceScoreRaw ?? null, 3),
    p.surfaceAbsoluteValueLabel ?? p.surfaceValueLabel ?? "",
    p.surfaceRelativeValueLabel ?? "",
    pctFmt(p.marketTermPercentile ?? null, 1),
    p.surfaceShapeLabel ?? "",
    p.tenorBucket ?? "",
    p.expiry,
    String(p.right ?? ""),
    numberFmt(p.strike, 0),
    String(p.dte),
    pctFmt(p.moneyness ?? null, 1),
    titleizeCode(p.moneynessBandStatus),
    String(p.conid),
    numberFmt(p.bid ?? null, 2),
    numberFmt(p.ask ?? null, 2),
    numberFmt(p.mid ?? p.midPrice ?? null, 2),
    currency(p.unitCost ?? ((p.mid ?? p.midPrice ?? 0) * (p.multiplier || 100))),
    numberFmt(p.vega ?? null, 3),
    p.openInterestStatus === "unavailable_verify" ? "Verify" : numberFmt(p.openInterest ?? null, 0),
    p.openInterestStatus || "known",
    titleizeCode(p.liquidityTier),
    numberFmt(p.sizingMaxInitialQty ?? p.oiCapQty ?? null, 0),
    p.sizingNote || "",
    ctx.marketRankOnly ? "—" : String(p.allocatedQty || 0),
    ctx.marketRankOnly ? "—" : currency(p.estNotional || 0),
    ctx.marketRankOnly ? "—" : numberFmt(p.estVegaUSDperPt || 0, 1),
  ];
}

export function htmlTable(headers: string[], rows: string[][]): string {
  return (
    `<table><thead><tr>${headers.map((h) => `<th>${escHtml(h)}</th>`).join("")}</tr></thead><tbody>` +
    rows.map((r) => `<tr>${r.map((c) => `<td>${escHtml(c)}</td>`).join("")}</tr>`).join("") +
    `</tbody></table>`
  );
}

/** Export value for a field the backend never sends: say so, never print a made-up default. */
export function reportedOr(value: any, fallback = "not reported"): string {
  return value === null || value === undefined || value === "" ? fallback : String(value);
}
